import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Truck, Car, Boxes, Route } from "lucide-react";
import { ToplamaRotasi } from "@/components/depo/ToplamaRotasi";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, GradButton } from "@/components/erp";
import { SelectBox } from "@/components/Inputs";
import api, { errMsg } from "@/lib/api";
import { KoliEditor, koliGovde } from "@/components/KoliEditor";
import { BarkodOkut, barkodEslestir, bip } from "@/components/BarkodOkut";
import { Rozet } from "@/components/ortak/Liste";

const DURUM_TON = { "Tamamlandı": "yesil", "Sevke hazır": "yesil", "Kısmen hazır": "sari", "Üretimde / Bekliyor": "mavi" };

const Tip = ({ aktif, onClick, icon: Icon, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm border transition-colors ${aktif ? "grad-bg text-white border-transparent" : "border-white/10 text-muted-foreground hover:text-white"}`}>
    <Icon className="w-4 h-4" />{children}
  </button>
);

const KalemSatir = ({ k, i, h, okunan, value, onChange }) => (
  <div className="grid grid-cols-[1fr_110px] gap-3 items-center rounded-xl bg-white/[0.04] p-3" data-testid={`sevk-kalem-${k.kod}`}>
    <div className="text-sm min-w-0">
      <div className="flex flex-wrap items-center gap-2"><span className="truncate"><span className="font-mono text-xs text-muted-foreground mr-2">{k.kod}</span>{k.ad}</span>
        {h && <Rozet ton={DURUM_TON[h.durum]} testid={`sevk-durum-${i}`}>{h.durum}</Rozet>}{h?.rfid_mod === "koli" && <Rozet ton="sari">RFID koli · {h.koli_adet}</Rozet>}{h?.rfid_mod === "tekli" && <Rozet ton="sari">RFID tekli</Rozet>}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">Sipariş {k.miktar} · sevk edilen {k.sevk_miktar || 0} · <span className="text-emerald-400">hazır {k.rezerve_miktar}</span> · kalan {+(k.miktar - (k.sevk_miktar || 0)).toFixed(4)} {k.birim}
        {okunan > 0 && <span className="text-[var(--g1)]" data-testid={`sevk-okunan-${i}`}> · okutulan {okunan}</span>}</div>
      {h && Object.keys(h.nitelikler).length > 0 && <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{Object.entries(h.nitelikler).map(([a, v]) => `${a}: ${v}`).join(" · ")}</div>}
    </div>
    <Input data-testid={`sevk-miktar-${i}`} type="number" step="any" min="0" max={k.rezerve_miktar} className="bg-white/5 text-right" value={value} onChange={(e) => onChange(e.target.value)} />
  </div>
);

export const SevkiyatDialog = ({ s: ilk, onClose, onSaved }) => {
  const [s, setS] = useState(ilk);
  const hazir = useMemo(() => s.kalemler.map((k, i) => ({ ...k, i })).filter((k) => k.rezerve_miktar > 1e-9), [s]);
  const [miktar, setMiktar] = useState(Object.fromEntries(hazir.map((k) => [k.i, k.rezerve_miktar])));
  const [harita, setHarita] = useState([]);
  const [okunan, setOkunan] = useState({});
  const [rotaAc, setRotaAc] = useState(false);
  useEffect(() => { api.get(`/siparisler/${s.id}/paketleme-haritasi`).then((r) => setHarita(r.data)).catch(() => {}); }, [s]);
  const [f, setF] = useState({ teslim_tipi: "kargo", kargo_firma: "", takip_no: "", plaka: "", sofor: "", teslim_adresi: "", koli_adedi: "", aciklama: "" });
  const [firmalar, setFirmalar] = useState({});
  const [yazdir, setYazdir] = useState(false);
  const [koliler, setKoliler] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  useEffect(() => { api.get("/kargo-firmalari").then((r) => setFirmalar(r.data)); }, []);

  const koliyeKoy = (i, adet, hh) => {
    const h = hh || harita[i] || {};
    setKoliler((kl) => {
      const l = [...kl];
      if (h.rfid_mod === "koli" && h.koli_adet > 0) {
        let kalan = adet;
        while (kalan > 1e-9) {
          let j = l.map((k, n) => (k.rfid === i && Number(k.kalemler[i] || 0) < h.koli_adet - 1e-9 ? n : -1)).filter((n) => n >= 0).pop();
          if (j === undefined) { l.push({ tip: "RFID koli", rfid: i, kalemler: {} }); j = l.length - 1; }
          const ic = Number(l[j].kalemler[i] || 0), koy = Math.min(kalan, h.koli_adet - ic);
          l[j] = { ...l[j], kalemler: { ...l[j].kalemler, [i]: +(ic + koy).toFixed(4) } };
          kalan -= koy;
        }
        return l;
      }
      let j = l.map((k, n) => (k.rfid === undefined ? n : -1)).filter((n) => n >= 0).pop();
      if (j === undefined) { l.push({ tip: "Orta koli", kalemler: {} }); j = l.length - 1; }
      l[j] = { ...l[j], kalemler: { ...l[j].kalemler, [i]: +(Number(l[j].kalemler[i] || 0) + adet).toFixed(4) } };
      return l;
    });
    setOkunan((o) => {
      const n = { ...o, [i]: +((o[i] || 0) + adet).toFixed(4) };
      setMiktar((m) => (Object.keys(o).length ? { ...m, [i]: n[i] } : { ...Object.fromEntries(Object.keys(m).map((x) => [x, 0])), [i]: n[i] }));
      return n;
    });
  };
  const siparisEkle = async (u, adet) => {
    if ((u.kullanilabilir || 0) + 1e-9 < adet) { bip(false); return toast.error(`${u.kod}: stokta kullanılabilir ${u.kullanilabilir || 0} ${u.birim} var, ${adet} eklenemez. Sipariş değiştirilmedi.`); }
    try {
      const kalemler = [...s.kalemler.map((k, i) => ({ urun_id: k.urun_id, miktar: k.miktar, birim_fiyat: k.birim_fiyat, kaynak_idx: i })), { urun_id: u.urun_id, miktar: adet }];
      const { data } = await api.put(`/siparisler/${s.id}/kalemler`, { kalemler });
      const yeniHarita = (await api.get(`/siparisler/${s.id}/paketleme-haritasi`)).data;
      setHarita(yeniHarita); setS(data);
      const yeni = data.kalemler.length - 1;
      if ((data.kalemler[yeni].rezerve_miktar || 0) + 1e-9 < adet) { bip(false); return toast.error("Siparişe eklendi ama stok ayrılamadı; bu sevkiyata konulamadı"); }
      koliyeKoy(yeni, adet, yeniHarita[yeni]);
      toast.success(`Siparişe ${adet} ${u.birim} eklendi ve koliye kondu · ${data.kalemler[yeni].kod}`);
    } catch (e) { bip(false); toast.error(errMsg(e)); }
  };
  const stokBilgi = (kod) => api.get(`/barkod/${encodeURIComponent(kod)}`).then((r) => r.data).catch(() => null);
  const okut = async (kod) => {
    const m = barkodEslestir(harita.filter((h) => hazir.some((k) => k.i === h.kalem_idx)), kod) || barkodEslestir(harita, kod);
    if (!m) {
      const u = await stokBilgi(kod);
      if (!u) { bip(false); return toast.error(`${kod} barkodlu ürün bulunamadı`); }
      bip(false);
      if (window.confirm(`${u.kod} · ${u.ad} bu siparişte yok. Siparişe ${u.carpan} ${u.birim} eklenip koliye konsun mu?`)) siparisEkle(u, u.carpan);
      return;
    }
    let kalan = m.carpan;
    const satirlar = hazir.filter((k) => k.urun_id === m.x.urun_id);
    for (const k of satirlar) {
      const bos = +(k.rezerve_miktar - (okunan[k.i] || 0)).toFixed(4);
      if (bos > 1e-9 && kalan > 1e-9) { const koy = Math.min(bos, kalan); koliyeKoy(k.i, koy); kalan = +(kalan - koy).toFixed(4); }
    }
    if (kalan <= 1e-9) { bip(true); return; }
    bip(false);
    const u = await stokBilgi(kod);
    if (u && window.confirm(`${m.x.kod}: ${m.carpan - kalan > 0 ? `${+(m.carpan - kalan).toFixed(4)} hazır satıra kondu; ` : ""}${kalan} ${m.x.birim} sipariş/hazır miktarını aşıyor. Fazlası siparişe eklenip koliye konsun mu?`)) siparisEkle(u, kalan);
  };
  const otoKoli = () => {
    const l = [], karisik = { tip: "Orta koli", kalemler: {} };
    hazir.forEach((k) => {
      const m = Number(miktar[k.i] || 0), h = harita[k.i] || {};
      if (m <= 0) return;
      if (h.rfid_mod === "koli" && h.koli_adet > 0) { for (let r = m; r > 1e-9; r -= h.koli_adet) l.push({ tip: "RFID koli", rfid: k.i, kalemler: { [k.i]: +Math.min(h.koli_adet, r).toFixed(4) } }); }
      else karisik.kalemler[k.i] = m;
    });
    setKoliler(Object.keys(karisik.kalemler).length ? [...l, karisik] : l);
  };
  const rfidVar = hazir.some((k) => harita[k.i]?.rfid_mod === "koli" && harita[k.i]?.koli_adet > 0);

  const save = async () => {
    const kalemler = hazir.map((k) => ({ kalem_idx: k.i, miktar: Number(miktar[k.i]) })).filter((k) => k.miktar > 0);
    if (!kalemler.length) return toast.error("En az bir kalem için sevk miktarı girin");
    setBusy(true);
    try {
      const { data } = await api.post("/sevkiyatlar", { ...f, siparis_id: s.id, kalemler, koliler: koliGovde(koliler), koli_adedi: f.koli_adedi === "" ? null : Number(f.koli_adedi) });
      toast.success(`${data.no} oluşturuldu${data.kismi ? " · kalan miktar siparişte bekliyor" : " · sipariş tamamen sevk edildi"}`);
      onSaved(data, yazdir);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass max-w-2xl max-h-[92vh] overflow-y-auto erp-scroll" data-testid="sevkiyat-dialog">
        <DialogHeader><DialogTitle className="font-head flex flex-wrap items-center gap-2">Sevkiyat · {s.no} · {s.cari_unvan}
          <button type="button" data-testid="sevk-toplama-rotasi" onClick={() => setRotaAc(true)} className="ml-auto mr-6 inline-flex items-center gap-1 px-3 h-8 rounded-full border border-emerald-500/50 text-emerald-300 text-xs font-normal hover:bg-emerald-500/10"><Route className="w-3.5 h-3.5" />Toplama rotası</button></DialogTitle></DialogHeader>
        {rotaAc && <ToplamaRotasi siparisId={s.id} onClose={() => setRotaAc(false)} onSevk={(v) => { setRotaAc(false); onSaved(v, false); }} />}
        <div className="rounded-xl border border-[var(--g1)]/30 bg-white/[0.03] p-3 space-y-2" data-testid="paketleme-haritasi">
          <div className="text-sm font-medium flex items-center gap-2"><Boxes className="w-4 h-4 text-[var(--g1)]" />Paketleme haritası <span className="text-xs text-muted-foreground font-normal">(isteğe bağlı: okutulan ürün son koliye eklenir, koli barkodu koli adedi kadar sayar)</span></div>
          <BarkodOkut testid="sevk-barkod" onKod={okut} />
          {rfidVar && <button type="button" data-testid="sevk-oto-koli" onClick={otoKoli} className="text-xs text-[var(--g1)] hover:underline">RFID koli ayarına göre kolilere otomatik dağıt</button>}
        </div>
        <div className="space-y-2">{hazir.map((k) => <KalemSatir key={k.i} k={k} i={k.i} h={harita[k.i]} okunan={okunan[k.i] || 0} value={miktar[k.i] ?? 0} onChange={(v) => setMiktar((m) => ({ ...m, [k.i]: v }))} />)}</div>
        {harita.filter((h) => !hazir.some((k) => k.i === h.kalem_idx) && h.kalan > 1e-9).map((h) => (
          <div key={h.kalem_idx} className="flex items-center gap-2 text-xs text-muted-foreground px-3" data-testid={`sevk-bekleyen-${h.kod}`}><span className="font-mono">{h.kod}</span>{h.ad}<Rozet ton={DURUM_TON[h.durum]}>{h.durum}</Rozet><span className="ml-auto">kalan {h.kalan} {h.birim}</span></div>))}
        <p className="text-xs text-muted-foreground">Yalnızca stokta ayrılmış miktar sevk edilebilir. Kalan miktar sipariş tamamlanana veya iptal edilene kadar sırada bekler.</p>
        <KoliEditor kalemler={hazir} miktar={miktar} koliler={koliler} setKoliler={setKoliler} />
        <div className="flex gap-2">
          <Tip testid="sevk-tip-kargo" aktif={f.teslim_tipi === "kargo"} onClick={() => set("teslim_tipi", "kargo")} icon={Truck}>Kargo</Tip>
          <Tip testid="sevk-tip-arac" aktif={f.teslim_tipi === "arac"} onClick={() => set("teslim_tipi", "arac")} icon={Car}>Kendi aracımız</Tip>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {f.teslim_tipi === "kargo" ? <>
            <Field label="Kargo firması *"><SelectBox testid="sevk-kargo-firma" value={f.kargo_firma} onChange={(v) => set("kargo_firma", v)} options={Object.entries(firmalar).map(([value, label]) => ({ value, label }))} /></Field>
            <Field label="Takip no (sonra da girilebilir)"><Input data-testid="sevk-takip-no" className="bg-white/5" value={f.takip_no} onChange={(e) => set("takip_no", e.target.value)} /></Field>
          </> : <>
            <Field label="Araç plakası *"><Input data-testid="sevk-plaka" className="bg-white/5 uppercase" value={f.plaka} onChange={(e) => set("plaka", e.target.value)} /></Field>
            <Field label="Şoför"><Input data-testid="sevk-sofor" className="bg-white/5" value={f.sofor} onChange={(e) => set("sofor", e.target.value)} /></Field>
          </>}
          {!koliler.length && <Field label="Koli / palet adedi"><Input data-testid="sevk-koli" type="number" min="0" className="bg-white/5" value={f.koli_adedi} onChange={(e) => set("koli_adedi", e.target.value)} /></Field>}
          <Field label="Teslim adresi (boşsa cari adresi)"><Input data-testid="sevk-adres" className="bg-white/5" value={f.teslim_adresi} onChange={(e) => set("teslim_adresi", e.target.value)} /></Field>
          <Field label="Açıklama" className="sm:col-span-2"><Input data-testid="sevk-aciklama" className="bg-white/5" value={f.aciklama} onChange={(e) => set("aciklama", e.target.value)} /></Field>
        </div>
        <label className="flex items-center gap-2.5 text-sm cursor-pointer select-none" data-testid="sevk-yazdir-label">
          <Checkbox data-testid="sevk-yazdir" checked={yazdir} onCheckedChange={(v) => setYazdir(!!v)} />İrsaliyeyi yazdır <span className="text-xs text-muted-foreground">(işaretsizse ekranda gösterilir)</span>
        </label>
        <div className="flex justify-end gap-2"><button onClick={onClose} className="px-5 py-2 rounded-full border border-white/10 text-sm" data-testid="sevk-vazgec">Vazgeç</button><GradButton onClick={save} disabled={busy} data-testid="sevk-kaydet"><Truck className="w-4 h-4" />Sevk et</GradButton></div>
      </DialogContent>
    </Dialog>
  );
};
